import Yup from 'utils/validation/yup-extensions';
import { getDefaultRenewalDate } from '../../../UserAccessReview/_util';
import {
	queryForCostCenters,
	getNonEmployeeUserTypes,
	getNonEmployeeUserSubtypes,
	getStudentColleges,
	getHospitals,
	getNonEmployeeJobTitles,
	validateManagersEmail,
	validateAccountCoordinatorEmail,
	getCountries,
	getStates,
	getDepartments,
	getBusinessUnitFromCostCenter,
	getYesNoOptions,
	getStudyTypes
} from './_api';

/**
 * UAR Status types
 */
export const STATUS_TYPES = {
	OVERDUE: 'OVERDUE',
	VALID_EXPIRING_SOON: 'VALID_EXPIRING_SOON',
	VALID: 'VALID',
};

/**
 * Keys to be used by Formik
 */
export const UAR_FIELD_KEYS = {
	USERS_LIST: 'usersList',
	RENEWAL_DATE: 'renewalDate',
	REVIEW_NON_EMPLOYEES: 'reviewNonEmployees',
	REVIEW_NON_EMPLOYEE_SUMMARY: 'reviewNonEmployeeSummary',
};

export const UAR_STEPPER_FIELDS = {
	SELECT_USER: 'Select User To Edit',
	PERSONNEL_INFORMATION: 'Personnel Information',
	HOME_ADDRESS: 'Home Address',
	USER_TYPE: 'User Type',
	SUBMIT: 'Submit',
};

export const UAR_STEPPER_VALUES = [
	{
		step: 0,
		field: {
			value: UAR_STEPPER_FIELDS.SELECT_USER,
		},
	},
	{
		step: 1,
		field: {
			value: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		},
	},
	{
		step: 2,
		field: {
			value: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		},
	},
	{
		step: 3,
		field: {
			value: UAR_STEPPER_FIELDS.USER_TYPE,
		},
	},
	{
		step: 4,
		field: {
			value: UAR_STEPPER_FIELDS.SUBMIT,
		},
	},
];

export const UAR_USER_PROFILE_KEYS = {
	JOB_TITLE: 'jobTitle',
	BUSINESS_UNIT: 'businessUnit',
	COST_CENTER: 'costCenter',
	DEPARTMENT: 'departmentName',
	MANAGERS_EMAIL: 'supervisorEmail',
	HOSPITAL_AFFILIATION: 'hospitalAffiliation',
	OFFICE_ADDRESS_1: 'officeAddress1',
	OFFICE_ADDRESS_2: 'officeAddress2',
	OFFICE_ADDRESS_3: 'officeAddress3',
	COUNTRY: 'country',
	CITY: 'city',
	STATE_PROVINCE: 'state',
	ZIPCODE_POSTALCODE: 'zipCode',
	PHONE_NUMBER: 'phoneNumber',
	ACCOUNT_COORDINATOR_EMAIL: 'accountCoordinatorEmail',
	ALTERNATE_EMAIL: 'alternateEmail',
	THIRD_PARTY_ORGANIZATION: 'thirdPartyOrganization',
	HOME_STREET_ADDRESS: 'homeStreet',
	HOME_COUNTRY: 'homeCountry',
	HOME_CITY: 'homeCity',
	HOME_STATE: 'homeState',
	HOME_ZIP_CODE: 'homeZipCode',
	USER_TYPE: 'userType',
	SPECIFY_OTHER: 'specifyOther',
	regulatoryUserTypeAgency: 'agency',
	ADDITIONAL_INFORMATION: 'researcherUserType',
	COLLEGE: 'studentUserTypeCollege',
	COLLEGE_SPECIALTY: 'studentUserTypeSpecialty',
	TYPEID: 'typeId',
	ESOID: 'esoId',
	SITE_ADMINISTRATOR: 'siteAdministrator',
	TRAINING: 'training',
	TRAINING_DATE: 'trainingDate',
	STUDY_TYPE: 'studyType',
	STUDY_ID: 'studyID'
	
};

// Form field metadata (default values, validation schema)
// for edit user profile
export const UAR_USER_PROFILE_FIELDS = {
	[UAR_USER_PROFILE_KEYS.JOB_TITLE]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Job Title',
		disableForEmployee: true,
		defaultValue: '',
		type: 'select', // This type will get over-ridden to normal text field when user is employee
		prePopulate: getNonEmployeeJobTitles,
		validationSchema: Yup.string().required('required'),
	},
	[UAR_USER_PROFILE_KEYS.BUSINESS_UNIT]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Business Unit',
		type: 'select',
		prePopulate: getBusinessUnitFromCostCenter,
		prePopulateDependency: UAR_USER_PROFILE_KEYS.COST_CENTER,
		defaultValue: '',
		validationSchema: Yup.string().nullable().required('Required'),
	},
	[UAR_USER_PROFILE_KEYS.COST_CENTER]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Cost Center',
		autoComplete: true,
		autoCompleteAPI: queryForCostCenters,
		defaultValue: '',
		validationSchema: Yup.string().nullable().required('Required'),
	},
	[UAR_USER_PROFILE_KEYS.DEPARTMENT]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Department',
		type: 'select',
		prePopulate: getDepartments,
		prePopulateDependency: UAR_USER_PROFILE_KEYS.COST_CENTER,
		defaultValue: '',
		validationSchema: Yup.string().nullable().required('Required'),
	},
	[UAR_USER_PROFILE_KEYS.MANAGERS_EMAIL]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: "Manager's Email",
		defaultValue: '',
		validationSchema: Yup.string()
			.email('Invalid email format')
			.required('Manager Email is Required')
			.test('Valid-UPMC-Email', async function (email) {
				if (email) {
					const result = await validateManagersEmail({
						email: email,
					});
					let valid = result?.data?.payload?.valid;
					let message = result?.data?.payload?.message;
					if (!valid) {
						return this.createError({
							message: message,
						});
					} else {
						return true;
					}
				} else {
					return this.createError({
						message: 'Valid email is required',
					});
				}
			}),
	},
	[UAR_USER_PROFILE_KEYS.HOSPITAL_AFFILIATION]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Hospital Affiliation',
		defaultValue: '',
		type: 'select',
		prePopulate: getHospitals,
		// validationSchema: Yup.array().min(1, "Please select a cost center."),
		validationSchema: Yup.string().required('Required'),
	},
	[UAR_USER_PROFILE_KEYS.OFFICE_ADDRESS_1]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Office Address 1',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.OFFICE_ADDRESS_2]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Office Address 2',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.OFFICE_ADDRESS_3]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Office Address 3',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.COUNTRY]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Country',
		type: 'select',
		defaultValue: '',
		prePopulate: getCountries,
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.CITY]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'City',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.STATE_PROVINCE]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'State/Province',
		type: 'select',
		defaultValue: '',
		prePopulate: getStates,
		prePopulateDependency: UAR_USER_PROFILE_KEYS.COUNTRY,
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.ZIPCODE_POSTALCODE]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Zip Code/Postal Code',
		defaultValue: '',
		validationSchema: Yup.string().matches(
			/^[a-zA-Z0-9\s\-]{3,10}$/,
			'Invalid zip code format'
		),
	},
	[UAR_USER_PROFILE_KEYS.PHONE_NUMBER]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Phone Number',
		defaultValue: '',
		validationSchema: Yup.string().matches(
			/^\+?[1-9]\d{0,2}[-.\s()]?\(?\d{1,4}\)?[-.\s()]?\d{1,4}[-.\s()]?\d{1,9}$/,
			'Invalid phone number'
		),
	},
	[UAR_USER_PROFILE_KEYS.ACCOUNT_COORDINATOR_EMAIL]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: "Account Coordinator / Site Admin Email",
		defaultValue: '',
		validationSchema: Yup.string()
			.email('Invalid email format')
			.required('Account Coordinator / Site Admin Email is Required')
			.test('Valid-UPMC-Email', async function (email) {
				if (email) {
					const result = await validateAccountCoordinatorEmail({
						email: email,
					});
					let valid = result?.data?.payload?.valid;
					let message = result?.data?.payload?.message;
					if (!valid) {
						return this.createError({
							message: message,
						});
					} else {
						return true;
					}
				} else {
					return this.createError({
						message: 'Valid email is required',
					});
				}
			}),
	},
	[UAR_USER_PROFILE_KEYS.ALTERNATE_EMAIL]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Alternate Email Address',
		defaultValue: '',
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return (
				val !== 'Regulatory User' &&
				val !== 'Tenant Badge Only' &&
				val !== '' &&
				val !== undefined
			);
		},
		validationSchema: Yup.string()
			.email('Invalid Email')
			.matches(
				/^(?!.*@upmc\.edu$)/,
				'Emails ending with @upmc.edu are not allowed'
			)
			.when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
				is: val =>
					val !== 'Regulatory User' &&
					val !== 'Tenant Badge Only' &&
					val !== '' &&
					val !== undefined,
				then: schema => schema.required('Alternate Email is Required'),
			}),
	},
	[UAR_USER_PROFILE_KEYS.THIRD_PARTY_ORGANIZATION]: {
		section: UAR_STEPPER_FIELDS.PERSONNEL_INFORMATION,
		stepLabel: 'Third-Party Organization',
		type:'thirdPartyOrganizationAutoComplete',
		defaultValue: '',
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.TYPEID,
		when: val => {
			return (
				val === 8 // non-employee user type
			);
		},
		validationSchema: Yup.object({
  			organizationId: Yup.string().required('Required'),
  			organizationName: Yup.string().required('Required'),
		}).nullable().required('Required'),
	},
	[UAR_USER_PROFILE_KEYS.HOME_STREET_ADDRESS]: {
		section: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		stepLabel: 'Street',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.HOME_COUNTRY]: {
		section: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		stepLabel: 'Country',
		type: 'select',
		defaultValue: '',
		prePopulate: getCountries,
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.HOME_CITY]: {
		section: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		stepLabel: 'City',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.HOME_STATE]: {
		section: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		stepLabel: 'State/Province',
		type: 'select',
		defaultValue: '',
		prePopulate: getStates,
		prePopulateDependency: UAR_USER_PROFILE_KEYS.HOME_COUNTRY,
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.HOME_ZIP_CODE]: {
		section: UAR_STEPPER_FIELDS.HOME_ADDRESS,
		stepLabel: 'Zip Code/Postal Code',
		defaultValue: '',
		validationSchema: Yup.string().matches(
			/^[a-zA-Z0-9\s\-]{3,10}$/,
			'Invalid zip code format'
		),
	},
	[UAR_USER_PROFILE_KEYS.USER_TYPE]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'User Type',
		type: 'select',
		defaultValue: '',
		prePopulate: getNonEmployeeUserTypes,
		// validationSchema: Yup.string(),
		// validationSchema: Yup.string().required('required'),
		[UAR_USER_PROFILE_KEYS.JOB_TITLE]: Yup.string()
			.test(
				'is-required',
				'User Type is required for non-employees',
				function (value) {
					const typeId = this.parent[UAR_USER_PROFILE_KEYS.TYPEID];
					return typeId?.toString() === '7' || !!value.toString();
				}
			)
			.required('required'),
	},
	[UAR_USER_PROFILE_KEYS.SPECIFY_OTHER]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Specify Other',
		defaultValue: '',
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Other';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Other',
			then: schema => schema.required('Please specify other user type'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.AGENCY]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Agency',
		type: 'select',
		defaultValue: '',
		required: true,
		prePopulate: getNonEmployeeUserSubtypes,
		prePopulateParam: 14,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Regulatory User';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Regulatory User',
			then: schema => schema.required('Please select a valid agency'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.ADDITIONAL_INFORMATION]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Additional Information',
		defaultValue: '',
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Researcher';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Researcher',
			then: schema =>
				schema.required('Please specify additional information'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.COLLEGE]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'College',
		type: 'select',
		defaultValue: '',
		required: true,
		prePopulate: getStudentColleges,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Student';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Student',
			then: schema => schema.required('Please select a valid college'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.TYPEID]: {
		section: '',
		stepLabel: 'Type ID',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.ESOID]: {
		section: '',
		stepLabel: 'ESO ID',
		defaultValue: '',
		validationSchema: Yup.string(),
	},
	[UAR_USER_PROFILE_KEYS.SITE_ADMINISTRATOR]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Site Administrator',
		type: 'checkbox',
		defaultValue: false,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val in getNonEmployeeUserTypes
		},
		validationSchema: Yup.boolean(),
	},
	[UAR_USER_PROFILE_KEYS.TRAINING]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'CITI Privacy and Security Training completed?',
		type: 'select',
		defaultValue: '',
		prePopulate: getYesNoOptions,
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Researcher' || val === 'Drug Company Clinical Trials Representative / Auditor';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Researcher' ||  val === 'Drug Company Clinical Trials Representative / Auditor',
			then: schema =>
				schema.required('Please select Yes or No'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.TRAINING_DATE]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Select Date',
		type: 'date',
		defaultValue: null,
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.TRAINING,
		when: val => {
			return val === 'Yes';
		},
		validationSchema: Yup.date().when(UAR_USER_PROFILE_KEYS.TRAINING, {
			is: val => val === 'Yes',
			then: schema =>
				schema.required('Please select a date'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.STUDY_TYPE]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Study Type',
		type: 'select',
		defaultValue: '',
		prePopulate: getStudyTypes,
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Researcher' || val === 'Drug Company Clinical Trials Representative / Auditor';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Researcher' || val === 'Drug Company Clinical Trials Representative / Auditor',
			then: schema =>
				schema.required('Please select a Study Type'),
		}),
	},
	[UAR_USER_PROFILE_KEYS.STUDY_ID]: {
		section: UAR_STEPPER_FIELDS.USER_TYPE,
		stepLabel: 'Study ID',
		defaultValue: '',
		required: true,
		dependentOn: UAR_USER_PROFILE_KEYS.USER_TYPE,
		when: val => {
			return val === 'Researcher' || val === 'Drug Company Clinical Trials Representative / Auditor';
		},
		validationSchema: Yup.string().when(UAR_USER_PROFILE_KEYS.USER_TYPE, {
			is: val => val === 'Researcher' || val === 'Drug Company Clinical Trials Representative / Auditor',
			then: schema =>
				schema.required('Please enter your Study ID'),
		}),
	}
};

/**
 * Form field metadata (default values, validation schema)
 */
export const UAR_FIELDS = {
	[UAR_FIELD_KEYS.USERS_LIST]: {
		stepLabel: 'Select User(s)',
		defaultValue: [],
		validationSchema: Yup.array().min(
			1,
			'Please select one or more users.'
		),
	},
	[UAR_FIELD_KEYS.RENEWAL_DATE]: {
		stepLabel: 'Pick Renewal Date',
		defaultValue: getDefaultRenewalDate()
			.toISOString('YYYY-MM-DD')
			.split('T')[0],
		validationSchema: Yup.date()
			.typeError('Please enter a valid date')
			.required()
			.test(
				'maxDate',
				'Renewal date can be no more than one year from the end of the current month',
				dateValue => {
					const cutoff = getDefaultRenewalDate();
					return dateValue <= cutoff;
				}
			),
	},
	[UAR_FIELD_KEYS.REVIEW_NON_EMPLOYEES]: {
		stepLabel: 'Review Non-Employee(s)',
		defaultValue: false,
		validationSchema: Yup.boolean().test(
			'isTrue',
			'Please acknowledge that you have reviewed non-employee access.',
			boolValue => {
				return boolValue;
			}
		),
	},
	[UAR_FIELD_KEYS.REVIEW_NON_EMPLOYEE_SUMMARY]: {
		stepLabel: 'Submission Summary',
		defaultValue: true,
		validationSchema: Yup.boolean(),
	},
};
